/**
 * Workspace configuration — resolved from `archlang.json` (if present)
 * layered over defaults. VSCode settings merge happens in the server
 * via `applyClientSettings`.
 *
 * An empty `archlang.json` is valid: it just marks the project root.
 */

import type { LspIO } from "../io.js";

export type Severity = "error" | "warning" | "info" | "off";

export interface FormatterConfig {
  readonly indent: number;
  readonly trailingCommas: boolean;
}

export interface ValidationConfig {
  readonly unusedInterface: Severity;
  readonly missingTeam: Severity;
  readonly unknownReference: Severity;
}

export interface ArchlangConfig {
  readonly include: readonly string[];
  readonly exclude: readonly string[];
  readonly formatter: FormatterConfig;
  readonly validation: ValidationConfig;
  /** Absolute filesystem path to the resolved project root, or undefined
   *  when running in zero-config mode (no archlang.json found). */
  readonly projectRoot?: string;
  /** True when an archlang.json was found. */
  readonly hasProjectFile: boolean;
}

export const DEFAULT_CONFIG: ArchlangConfig = {
  include: ["**/*.arch"],
  exclude: ["node_modules/**", "dist/**", ".git/**"],
  formatter: { indent: 2, trailingCommas: true },
  validation: {
    unusedInterface: "info",
    missingTeam: "warning",
    unknownReference: "error",
  },
  hasProjectFile: false,
};

export interface RawConfig {
  include?: readonly string[];
  exclude?: readonly string[];
  formatter?: Partial<FormatterConfig>;
  validation?: Partial<ValidationConfig>;
}

export function mergeConfig(
  base: ArchlangConfig,
  raw: RawConfig | undefined,
  overrides: { projectRoot?: string; hasProjectFile?: boolean } = {}
): ArchlangConfig {
  if (!raw) {
    return {
      ...base,
      projectRoot: overrides.projectRoot ?? base.projectRoot,
      hasProjectFile: overrides.hasProjectFile ?? base.hasProjectFile,
    };
  }
  return {
    include: raw.include ?? base.include,
    exclude: raw.exclude ?? base.exclude,
    formatter: {
      indent: raw.formatter?.indent ?? base.formatter.indent,
      trailingCommas:
        raw.formatter?.trailingCommas ?? base.formatter.trailingCommas,
    },
    validation: {
      unusedInterface:
        raw.validation?.unusedInterface ?? base.validation.unusedInterface,
      missingTeam: raw.validation?.missingTeam ?? base.validation.missingTeam,
      unknownReference:
        raw.validation?.unknownReference ?? base.validation.unknownReference,
    },
    projectRoot: overrides.projectRoot ?? base.projectRoot,
    hasProjectFile: overrides.hasProjectFile ?? base.hasProjectFile,
  };
}

/**
 * Walk up from `rootUri` looking for `archlang.json`. Returns the raw
 * (unmerged) config plus the URI of the file it was loaded from, or
 * `{ raw: null, configUri: null }` when no project file was found.
 *
 * Callers merge with `DEFAULT_CONFIG` via `mergeConfig` and supply the
 * `projectRoot` / `hasProjectFile` overrides themselves.
 */
export async function loadProjectConfigFromUri(
  io: LspIO,
  rootUri: string
): Promise<{ raw: RawConfig | null; configUri: string | null }> {
  let dir = rootUri;
  for (;;) {
    const candidate = io.joinUri(dir, "archlang.json");
    const text = await io.readFile(candidate);
    if (text !== null) {
      const raw = parseJson(text);
      return { raw: raw ?? null, configUri: candidate };
    }
    const parent = io.dirname(dir);
    if (parent === dir) break;
    dir = parent;
  }
  return { raw: null, configUri: null };
}

function parseJson(text: string): RawConfig | undefined {
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as RawConfig;
    }
  } catch {
    // invalid JSON — fall through to defaults
  }
  return undefined;
}

/** Test helper — locate the project file URI next to a folder. */
export function projectFileUri(io: LspIO, folderUri: string): string {
  return io.joinUri(folderUri, "archlang.json");
}

/**
 * Glob matcher adequate for the include/exclude patterns archlang users
 * actually write (`**\/*.arch`, `dist/**`, `node_modules/**`). Simpler
 * than pulling in a full glob library.
 */
export function matchesAny(
  relativePath: string,
  patterns: readonly string[]
): boolean {
  return patterns.some((p) => matchGlob(relativePath, p));
}

export function matchGlob(pathStr: string, pattern: string): boolean {
  const regex = globToRegex(pattern);
  return regex.test(pathStr);
}

function globToRegex(pattern: string): RegExp {
  // Translate a small subset: `**` → any path, `*` → any non-slash, `?` → single char.
  let out = "^";
  let i = 0;
  while (i < pattern.length) {
    const ch = pattern[i]!;
    if (ch === "*") {
      if (pattern[i + 1] === "*") {
        out += ".*";
        i += 2;
        if (pattern[i] === "/") i++;
      } else {
        out += "[^/]*";
        i++;
      }
    } else if (ch === "?") {
      out += "[^/]";
      i++;
    } else if (".+()|^$[]{}\\".includes(ch)) {
      out += "\\" + ch;
      i++;
    } else {
      out += ch;
      i++;
    }
  }
  out += "$";
  return new RegExp(out);
}
